import type { DestinationId } from '../app/destinations.js';
import type { VisionImage } from '../types/channel.js';
import type { TaskOutputDoc } from '../types/task-output-doc.js';
import type { ClientToolSpec, MemoryMode } from '../platform/deployment.js';

export type SurfaceId = 'voice_card' | 'composer' | 'command_k' | 'slack' | 'mobile' | 'moment_card' | 'telegram' | 'whatsapp' | 'imessage' | 'email' | 'api' | 'phone';

export type ToolName =
  | 'create_task'
  | 'create_computer_task'
  | 'fetch_recent_tasks'
  | 'follow_up_task'
  | 'answer_task'
  // Approve by voice (T8c): settle what a task waits on them to GRANT, after a read-back and on their own yes or no.
  | 'approve_task'
  | 'save_to_nami'
  | 'show_tasks'
  | 'control_app'
  | 'web_fetch'
  // provider-NATIVE web search (no capability runs server-side): surfaced as
  // tool_started the moment the model goes out on the live web, so presence
  // surfaces can say "Searching" instead of a generic thinking state
  | 'web_search'
  // Composer Eyes (Slice 1): composer-direct OS perception — read the user's
  // Mac inline over the local-runner relay (like web_fetch reads a page).
  // Read-only AX, non-terminal; actuation stays on the task lane.
  | 'os_apps'
  | 'os_snapshot'
  | 'os_find'
  // Composer Eyes rung 3: one screenshot of an app window, read as pixels —
  // for visual questions AX text cannot answer, and the automatic fallback
  // when a heavy window blows the AX budget (07-23 Warp incident).
  | 'os_look'
  // Composer Eyes phase SEE: fine-grained inline reads over the local-runner
  // relay — the exact SELECTED text (os_selection), the element under the mouse
  // pointer (os_pointer), the WHOLE document/window as text with an automatic
  // AX→OCR→screenshot ladder (os_read), and pixels-as-text OCR for content AX
  // cannot serve (os_ocr). All read-only, non-terminal; gated behind OS_SEE_V1.
  | 'os_selection'
  | 'os_pointer'
  | 'os_read'
  | 'os_ocr'
  // Composer Hands (Slice 2): dispatch a COMPLEX desktop errand to an agent on
  // the user's Mac (which carries the full os-hands suite). Terminal — creates
  // a task; the desktop sibling of create_computer_task.
  | 'control_computer'
  // Tier-0 verbs (Composer lane): run one fast deterministic Mac verb inline
  // over the local-runner relay — system verbs taskless, app verbs iff a
  // persisted per-app grant exists, else it delegates to control_computer.
  | 'run_verb'
  // Tier-0 verb discovery (Composer lane, scale plane): find a verb id by the
  // user's words when run_verb's inline hot set has no match. Read-only,
  // non-terminal, no relay; gated with run_verb so the pair travel together.
  | 'search_verbs'
  // Dictate (hand-eye, Composer lane): type USER-AUTHORED text (verbatim
  // dictation or a canonical short reply) into an app on the user's Mac inline
  // over the local-runner relay, optionally pressing Return. Content fidelity
  // is enforced server-side — this lane never types model-composed text.
  | 'dictate'
  // Interact (hand-eye, Composer lane): perform ONE user-directed control
  // action (a table-locked key/scroll intent: play, pause, scroll, back, undo,
  // escape, select-all, enter) on the app the user is looking at, inline over
  // the local-runner relay. The action is chosen from a deterministic map —
  // never a model-authored keystroke. Ships dark behind COMPOSER_INTERACT_V1.
  | 'interact'
  // Click (pointer A1, Composer lane): click ONE control the user NAMES on the
  // app they are looking at ("click send", "the second result"), inline over
  // the interact relay. The target is the USER'S WORDS verbatim — never a
  // coordinate, never an invented label; the sidecar resolves it against the
  // real AX tree and refuses honestly. Ships dark behind OS_CLICK_V1.
  | 'click'
  // Fill (pointer A2, Composer lane, phase ACT): put a USER-AUTHORED value in
  // a field the user NAMES ("put vestra.ai in the search field"), inline over
  // the interact relay. The field name rides verbatim like click's target; the
  // text rides `value`, authored by the user. Ships dark behind OS_FILL_V1.
  | 'fill'
  // Menu (pointer A3, Composer lane, phase ACT): choose a menu item the user
  // NAMES, by single title or full path ("export as PDF from the file menu"),
  // inline over the interact relay; the sidecar resolves it against the real
  // menu bar and refuses honestly. Ships dark behind OS_MENU_V1.
  | 'menu'
  // Work in app (T8d, Composer lane): a short multi-step job inside ONE app on
  // the user's Mac, done by the Jev step lane on their runner over the lane
  // relay, no task. The model writes the goal and the words to type; it never
  // sends, deletes or pays (it stops and returns the question to ask).
  // Non-terminal; ships dark behind COMPOSER_WORK_IN_APP_V1.
  | 'work_in_app'
  // App recipes (T9, Composer lane): one closed in-app act on the user's Mac
  // ("go to LD in Slack", "new tab", "open my chat with Priya in WhatsApp"),
  // walked by the runner in one dial. A draft is typed unsent and asks "Send
  // it?"; only the person's own yes sends it. Non-terminal; ships dark behind
  // COMPOSER_APP_RECIPES_V1.
  | 'run_recipe'
  // Undo-last (trust pair, Composer lane): take back the LAST act the hands
  // performed ("undo that") by inverting the undo ring's most recent entry
  // over the interact relay — a CLOSED inversion table (scroll back, close the
  // new tab, undo keystroke for typing); non-invertible acts refuse honestly.
  // Rides interact's gates (no new flag).
  | 'undo_last'
  // Memory recall (Composer lane): search the user's Nami memory server-side —
  // the SAME corpus the sandbox's user.md projection renders (profile + recent
  // notes + dated moments + procedures) — and return the best-matching passages
  // inline. save_to_nami's read twin. Read-only, non-terminal; gated behind
  // COMPOSER_RECALL_V1.
  | 'recall_from_nami'
  // Task reading (Composer lane, phase SEE): the composer's parity with what
  // the app already shows about a task. task_output serves the distilled
  // output doc, the full answer and a creations summary in ONE call (the
  // common question, one round); task_state is the cheap "what is it doing";
  // task_creations lists the files a task made; task_transcript reads a
  // bounded slice of its turns. All read-only, non-terminal, ownership-gated;
  // all four gated behind COMPOSER_TASK_TOOLS_V1.
  | 'task_output'
  | 'task_state'
  | 'task_creations'
  | 'task_transcript'
  // Stop (Composer lane): end the CURRENT TURN of a task the composer started.
  // Terminal, ownership-gated through task-ref. It rides task-stop.service, the
  // same function the web Stop button calls, so one stop reaches the turn AND
  // unblocks every open tab — the frame and the terminals never fork.
  | 'stop_task'
  // Quiet (Composer 2.0 phase 2): end the moment with nothing sent, on
  // purpose. Terminal; served on the chat channels behind COMPOSER_QUIET_V1.
  | 'stay_silent'
  // Quiet (Composer 2.0 phase 2): put one of six reactions on the person's own
  // message instead of a sentence. Non-terminal, at most once per moment;
  // served only where the turn can react (chat channels), never web or notch.
  | 'react'
  // Promises (Composer 2.0 phase 3): record a promise the mind is making so it
  // is kept after the conversation moves on. Core, non-terminal; served on
  // every surface behind COMPOSER_PROMISES_V1.
  | 'promise'
  // Recall over the whole log (Composer 2.0 phase 4): search the person's own
  // lifelong thread for dated passages the tail no longer holds. Core,
  // non-terminal, read-only; served on every surface behind COMPOSER_DOSSIER_V1.
  | 'recall_from_thread'
  // Toolsets (Composer 2.0 phase 5): the extended catalog's two doors. Core on
  // every surface behind COMPOSER_TOOLSETS_V1. find_tools returns matching
  // tools as text (id, one line, what people say to reach it), filtered by
  // surface, flags and grants BEFORE ranking, and never changes the served
  // list (I6). run_tool executes one visible tool by id through the same
  // handler and the same enabledFor gate a direct call hits, and inherits the
  // target's terminality.
  // Watches (Composer 2.0 phase 6): a standing watch that speaks only when it
  // crosses; start, stop, list. Core, non-terminal; served on every surface
  // behind COMPOSER_PROACTIVE_V1.
  | 'watch'
  | 'find_tools'
  | 'run_tool'
  // Tool search (T11, COMPOSER_TOOL_ROUTER_V1 = search | presearch): the doors beside find_tools and
  // run_tool, served only in those modes. get_tool_schemas returns the exact records of tools the model
  // already knows by id; connect_app starts the existing connect flow for one of their apps (the
  // vestra.apps.connect card, so its event reads as that card's run).
  | 'get_tool_schemas'
  | 'connect_app'
  // Composer panels (spec 2026-09-19): put a result on the screen as a card
  // (show), or open one of the composer's own panels (open_panel: the history,
  // the tray, or close whatever card is up). Both non-terminal; served only on
  // the surfaces that draw panels, behind COMPOSER_PANELS_V1.
  | 'show'
  | 'open_panel'
  // The Line's reflex (spec 2026-09-21): end the voice call. Terminal by its
  // action; served ONLY on a spoken composer turn (the Line's own mark) behind
  // COMPOSER_LINE_REFLEX_V1, so no other surface can be handed a tool that
  // hangs up. Whatever the spoken matcher misses, a mind that has decided the
  // person is leaving must be able to do it rather than only say goodbye.
  | 'end_call'
  // Slack rooms (spec 2026-09-28 §7.2): stop answering the un-mentioned follow-ups in this thread until the next
  // mention. Served only on a Slack room turn, through the hand that turn carries (ChannelActs.muteThread).
  | 'mute_thread'
  // Slack (spec 2026-10-02 §4.6): put one of the owner's creations into this thread or DM as a file, under Bash's own
  // name, with no yes. Served only on a Slack turn, through the hand that turn carries (ChannelActs.attach).
  | 'attach'
  // Slack (spec 2026-10-02 §4.6): post in a channel Bash is a member of, now or at a time, under Bash's own name with a
  // line saying who asked, with no yes. Served only on a Slack turn, through the hands it carries (ChannelActs.post,
  // ChannelActs.schedule).
  | 'post'
  | 'schedule'
  // Voice replies on channels (spec 2026-10-03 §4.4): this turn's answer also goes out as a voice note. Served only on a
  // chat turn that carries the speak hand (ChannelActs.speakReply), which the turn holds only where a voice note can follow.
  | 'speak_reply'
  // Bash's phone (spec 2026-10-14 §4.2, P1b): Bash calls the person now on their verified number. Served on the app and
  // the chats behind BASH_PHONE_V1, on every turn a call can be asked from; the door says why a ring was not placed.
  | 'ring_me'
  // Phone errands (spec 2026-10-14 §4.4, PR 1): Bash calls someone else for the person, does the errand and reports
  // back where they asked. Served where ring_me is, behind BASH_PHONE_ERRANDS_V1 too; the door says why a call was not
  // placed.
  | 'place_call'
  // Contacts, Bash's phonebook (phone errands PR 2): keep someone ("save Ramesh, 98990 17734") and look one up. Served
  // where place_call is; save_contact is held on a turn from a call (ToolContext.fromCall), find_contact reads there too.
  | 'save_contact'
  | 'find_contact';

export type RouterErrorCode =
  | 'bad_tool_args'
  | 'loop_exhausted'
  /** The wallet is empty: the router never ran (server credits gate, 2026-09-02). */
  | 'credits_exhausted'
  | 'model_timeout'
  | 'tool_failed'
  | 'unauthorized'
  | 'internal';

export type RouterAction =
  /** `model` = what actually runs the task — the composer receipt shows it
   *  (the honest replacement for the pre-pick pill's claim). Optional so
   *  older servers/actions stay valid. */
  | {
      kind: 'task_created';
      taskId: string;
      title: string;
      model?: string;
      /** Set when the task runs on a model other than the user's pick (e.g. a
       *  text-only pick on the computer lane) — surfaces label the swap so a
       *  substitution is never silent. */
      substitutedFrom?: string;
      /** The dispatched ask (the user-facing displayText) and its persisted
       *  messages-row id. The action is emitted only AFTER dispatch persisted
       *  the row, so a client may seed the task page from these and paint the
       *  first frame complete; MESSAGE_SYNC later merges by the same row id.
       *  Optional so older servers/actions stay valid. */
      prompt?: string;
      messageId?: string;
      /** The one-line human sentence the model wrote for THIS request, sent to
       *  the user instead of a stock receipt. Optional on the type so an older
       *  server's action stays valid; required in the tool's input schema so a
       *  current model must supply it. */
      say?: string;
      /** The turn's files that rode into the task with this dispatch (their
       *  `fileId`s, server-verified, the person's own). The Line counts a staged
       *  file named here as taken even when the turn fails after starting the
       *  task, so it is never sent twice. Absent when none rode. */
      fileIds?: string[];
    }
  | {
      kind: 'task_followup';
      taskId: string;
      title: string;
      /** The persisted messages-row id of the forwarded turn, exactly as
       *  `task_created` carries it. A channel surface owes this turn's answer
       *  back to the chat that asked, and this is the id it keys that on.
       *  Optional so older servers/actions stay valid. */
      messageId?: string;
      /** The one-line human sentence the model wrote for THIS request, sent to
       *  the user instead of a stock receipt. Optional on the type so an older
       *  server's action stays valid; required in the tool's input schema so a
       *  current model must supply it. */
      say?: string;
      /** As `task_created` carries it: the turn's files that rode into the task. */
      fileIds?: string[];
    }
  /**
   * `say`: the save's own sentence, server-written, set only when the person's whole message was
   * one link (save-to-nami.ts): the save says what it did and asks what to do with the link, where
   * a quiet save left them hearing "Done." and their thread with no reply.
   */
  | { kind: 'nami_saved'; proposalId: string; summary: string; say?: string }
  /** Native-shell verb: open the notch task shelf ("show my tasks"). */
  | { kind: 'show_tasks' }
  /**
   * Native-shell verb (M2c): steer the MAIN APP window, bring it forward
   * already navigated to `route`. `route` is computed server-side from the
   * destination registry (packages/shared/src/app/destinations.ts, never
   * model-authored); the desktop additionally allowlists route prefixes
   * before navigating. `view` is the destination's id.
   *
   * `say` is the action's own sentence for the Line, as task_followup has: set
   * only when the open needs more than "Opening <label>." (a by-id miss that
   * landed on the list says it could not find the thing). Server-written, in
   * the second person; absent on an ordinary open.
   */
  | { kind: 'app_control'; view: DestinationId; route: string; label: string; say?: string }
  /**
   * The composer catalog, train 2a (T2-6): open a link the SERVER minted from a
   * vendor's redirect (a connect flow), never model-authored. The web opens it
   * in a new tab and shows it as a tap when the browser blocks the open; the
   * desktop opens it in the system browser behind its own https belt
   * (overlay.rs open_url_allowed); a chat sends `label: url` as text.
   *
   * NOT the `system.open_url` OS verb (sandbox os-verbs.ts), which is a
   * different lane entirely: that one the model authors, and it opens a URL on
   * the person's own machine through the local runner. Same two words, no
   * relation.
   *
   * `toolkit`: the app the link connects (the connect card's slug), so a chat
   * that draws its own card names it (Slack, spec 2026-10-02 §4.8). Every
   * other surface reads `label` and `url` as before.
   */
  | { kind: 'open_url'; url: string; label: string; toolkit?: string }
  /**
   * The Line's reflex (spec 2026-09-21): END THE CALL. Carries nothing — there
   * is one call, and it is the one this turn is being spoken on.
   *
   * The Line's action arm reads it and calls the hang-up it already had, which
   * says the goodbye and closes the row naming who ended it. Everywhere else it
   * is a no-op by design: every other surface already ignores an action kind it
   * does not know, and on a call what the person sees is the call ending, never
   * a receipt about it.
   */
  | { kind: 'end_call' };

/**
 * The composer catalog, train 2a (T2-9): what a run_tool call is running, so
 * the Line narrates and the pill files by family and effect. Present only on
 * a run_tool call under COMPOSER_CATALOG_V1 (a card id exists only there).
 */
export interface RunTarget {
  id: string;
  family: 'vestra' | 'app' | 'mac' | 'channel';
  effect: 'read' | 'reversible' | 'irreversible';
}

/** Composer panels (spec 2026-09-19 §3.1): where a sheet's source row stands, as its chip reads it. */
export type PanelSourceStatus = 'queued' | 'running' | 'needs' | 'done' | 'stopped' | 'out_of_credits' | 'error';

export type PanelSource =
  | { kind: 'task'; id: string; label: string; status: PanelSourceStatus }
  | { kind: 'creation' | 'file'; id: string; label: string };

export interface PanelDetail { label: string; value: string; tone?: 'quote' | 'danger' }

/** Who's waiting on the owner (@bash in Slack, spec 2026-10-02 §8.1): where one thing was read from. */
export type WaitingSource = 'slack' | 'gmail' | 'memory' | 'bash';
/**
 * One thing waiting on the owner, read at ask time. `key` is a structured ref (interaction:<id>, hold:<id>, offer:<id>,
 * pool:<id>, gmail:<thread>, slack:<tenant>:<C>:<ts>), the one thing the owner's Done or Snooze stores; `what` is shown
 * on the card and never stored (a colleague's words, for a mention or an email).
 */
export interface WaitingItem { key: string; source: WaitingSource; who: string; what: string; where: string | null; link: string | null; at: string | null }
/** What the read checked: read, not connected (skipped, and said so), or failed alone. */
export interface WaitingChecked { source: WaitingSource; state: 'ok' | 'not_connected' | 'failed' }

/**
 * Composer panels (spec 2026-09-19 §3.1): something the composer PUTS ON THE
 * SCREEN while it keeps talking, or as the thing it has to say. A panel never
 * routes and never ends the turn (an action does both). Every panel is minted
 * by the SERVER from server-read facts: the model never writes a URL, an offer
 * ref, a run id, or a doc that did not pass parseOutputDoc.
 *
 * `words` is compulsory: the plain-text twin for a surface with no screen (a
 * chat, the no-chat rail); a panel is never the only carrier of meaning.
 * `close` is the seventh member: the "close it" reflex on the live surface
 * needs a frame to ride, and it clears every panel a client holds.
 */
export type ComposerPanel =
  | { kind: 'sheet'; doc: TaskOutputDoc; source?: PanelSource; words: string }
  | { kind: 'history'; words: string }
  | { kind: 'tray'; words: string }
  | { kind: 'close'; words: string }
  | { kind: 'connect'; toolkit: string; displayName: string; authUrl: string; mode?: 'add'; accountHint?: string; words: string }
  | { kind: 'confirm'; offerRef: string; title: string; details: PanelDetail[]; verb: string; danger: boolean; words: string }
  | { kind: 'receipt'; title: string; meta: string; runId?: string; undoUntil?: string; words: string }
  /** Who's waiting on the owner (spec 2026-10-02 §8.1): Slack alone draws it (the DM card, Done and Snooze per item), so
   *  it is not in PANEL_KINDS and every other client passes it by and says its words. `at` is when it was read, `zone`
   *  the owner's, for "up to 9:10 AM" and a Snooze's tomorrow at 9:00. `more` is how many it found past the items it
   *  lists (the card says "and N more"). Its words never carry a colleague's. */
  | { kind: 'waiting'; items: WaitingItem[]; more?: number; checked: WaitingChecked[]; at: string; zone: string; words: string };

export type ComposerPanelKind = ComposerPanel['kind'];

/** The closed list a client draws, in the order the union declares them; a client ignores any kind not here ('waiting' is Slack's alone). */
export const PANEL_KINDS: readonly ComposerPanelKind[] = ['sheet', 'history', 'tray', 'close', 'connect', 'confirm', 'receipt'];

export function isPanelKind(k: unknown): k is ComposerPanelKind {
  return typeof k === 'string' && (PANEL_KINDS as readonly string[]).includes(k);
}

/** The undo route's answer (`POST /api/composer/runs/:runId/undo`): whether it ran, and the sentence to show. */
export interface ComposerUndoResponse { ok: boolean; words: string }

export type RouterEvent =
  /**
   * `agent` (train 1b) rides ONLY when the request named a handle: who
   * answered, or `null` when the server fell back to the lead, with `note`
   * saying why in one person-facing line. A request with no handle gets the
   * two-key frame it always got.
   */
  | { type: 'thread'; threadId: string; agent?: { handle: string; name: string } | null; note?: string }
  | { type: 'text_delta'; text: string }
  /** `runId` (T10): the id run_tool was asked to run, whatever it is (a card, an app tool, or a capability such as os_ocr), set only with
   *  COMPOSER_TOOL_ROUTER_V1 on, for the tool router's shadow line to score the door costs. Clients ignore it; off, it is never there.
   *  `ownRoom`: this call is a Slack room turn's read of its own channel (room-own-channel.ts), the one app read that is not
   *  personal there, so Slack's live checklist may name it. Set only on such a call; every other surface never sees it. */
  | { type: 'tool_started'; tool: ToolName; target?: RunTarget; runId?: string; ownRoom?: true }
  | { type: 'action'; action: RouterAction }
  /** Composer panels (spec 2026-09-19): a card to draw beside the words. Emitted BEFORE the text it accompanies; never terminal; an unknown kind is ignored by every client. */
  | { type: 'panel'; panel: ComposerPanel }
  | { type: 'error'; code: RouterErrorCode; message: string }
  /**
   * `superseded` (Composer 2.0 phase 5, web composer and notch only): the
   * person sent a NEW ask while this turn was running and this turn had not
   * yet started work, so it was cancelled and the new ask took its place.
   * Nothing from this turn was persisted or delivered; the replacement turn
   * carries the answer. A client renders it as "superseded", never as "not
   * sent" and never as an error. It may arrive as the ONLY event of a stream
   * (a queued ask superseded before it started has no `thread` to name).
   * Additive on the wire: older clients read an unknown reason as a plain end.
   */
  /**
   * `personal` (spec 2026-09-28 §17.10): the labels of the private sources this turn read ('Gmail', 'your memory'),
   * first-seen order, each once, present only when there is at least one. A Slack room holds the answer for the
   * person's tap when it is there. Additive on the wire: every other client ignores it.
   */
  | { type: 'done'; reason: 'answered' | 'routed' | 'superseded'; personal?: string[] };

/** One file the user attached, as the composer turn sees it. */
export interface ComposerAttachment {
  /**
   * The `user_files` row id — the durable reference that outlives the turn.
   * ALWAYS SERVER-MINTED: a channel stores before it asks, and the composer's
   * HTTP door ingests inline base64 through the same upload path before
   * converse runs. The door does NOT accept this field off the wire, and no
   * consumer may start trusting a client-supplied one: an id names a row in
   * someone's files, so an unverified claim is an IDOR waiting for its first
   * reader. A surface that points at an already-uploaded file sends
   * {@link storageUrl} instead, and the door derives the id from it
   * (attachmentFileId) only after the gate lets the caller read the file it
   * names (their own, or shared with them).
   *
   * Optional because a PRODUCER may not have one yet (a browser holds pixels,
   * not a row) — never faked, because "this attachment has no file row" is a
   * true fact about the world at that point, not a gap to paper over.
   */
  fileId?: string;
  /**
   * WIRE ONLY: the storage URL, or the /v door, of a file the surface already
   * has (the web typing box, the call's attach seat, a file shared with them). A
   * candidate, never an authority: the door reads the id off it, asks the gate
   * whether the caller may read it, and either sets `fileId` or names the entry
   * `unreadable`. Either way this field is dropped there, so no turn ever
   * carries it and nothing downstream reads it
   * (#bugs 2026-09-24: a video attached to a composer ask reached nobody, because
   * the id the web sent was rightly ignored and nothing took its place).
   */
  storageUrl?: string;
  filename: string;
  mediaType: string;
  /**
   * OPTIONAL, and only ever absent on an {@link unreadable} entry. Several
   * vendors declare no length on an inbound envelope, so a file we could not
   * take in may genuinely have no size to report — and `0` is not that. A
   * turn's naming line is written into a LIFELONG thread, so "kitchen.jpg
   * (0 B)" is a false durable record of a file that is not zero bytes, which is
   * the same class as claiming an image was "in their Files" when it
   * reached nobody. Absent means unknown, and the naming line says nothing
   * about size rather than something wrong.
   */
  sizeBytes?: number;
  /**
   * Present only for images within the vision cap — the SERVER enforces that,
   * at whichever door the picture came in (media-ingest's toVisionImage), so a
   * producer's own compression is a courtesy, never the guarantee. Rides THIS
   * turn as pixels and is dropped before the thread is persisted
   * (composer.service's redactImagesForPersist) — look once, remember by name.
   */
  vision?: VisionImage;
  /**
   * The attachment reached the turn but could NOT be taken in: refused on
   * size, the store failed, or the ingest ran out of its deadline. It is still
   * NAMED in the turn's text, as unread — a composer that answers confidently
   * about a picture nobody gave it is the exact failure this rail exists to
   * end, and silence is what produces that. Never set alongside a fileId.
   */
  unreadable?: true;
  /**
   * What was SAID in this attachment — set only for a voice note the vendor
   * marked as someone talking, never for an audio file they uploaded.
   *
   * A voice note IS the message, so when the sender typed nothing this text
   * becomes the turn's own `input` and the field is dropped from the
   * attachment: the same sentence twice would read as emphasis nobody meant.
   * It survives here only when the sender ALSO wrote a caption — then the
   * caption is the ask and this is named beside the recording, because spoken
   * words that reach us and go nowhere are the failure this rail exists to end.
   *
   * Absent when transcription failed or found no speech. The recording is
   * stored either way; a vendor's bad minute must never cost the file.
   */
  transcript?: string;
}

/**
 * Platform API v1 (the /v1 door): what a company's turn carries. Set ONLY by the /v1 adapter
 * (apps/server/src/platform/conversations); the web converse route builds its request field by
 * field and never copies one off the wire.
 */
export interface ConversePlatformContext {
  orgId: string;
  mode: 'live' | 'test';
  deploymentId?: string;
  deploymentVersion?: number;
  companyLayer?: string;
  clientTools?: ClientToolSpec[];
  outputSchema?: Record<string, unknown>;
  handOff: 'auto' | 'always';
  contextFence?: string;
  /** No end user: the turn runs on the deployment's shared service user, so its tools read nothing across conversations (master ruling 32). */
  headless: boolean;
  /**
   * What the company lets Vestra remember on this turn: the mode its work is stamped with (deployment, then org, then vestra;
   * task-platform.ts). Only 'vestra' reads Nami's user doc and distilled lines; absent keeps nothing. Vestra's own users (the
   * test gate's customer plays every scenario of every run) are 'none'.
   */
  memoryMode?: MemoryMode;
  /**
   * The user's record across conversations (the composer dossier, the ledger of all their work) is kept and read on this turn:
   * the turn's mode and the org's are both 'vestra' (platform/memories/user-record.ts). Absent, the turn reads no dossier and
   * its ledger is this conversation's own work.
   */
  userRecord?: true;
}

export interface ConverseRequest {
  input: string;
  /**
   * Files attached to this turn. `input` MAY be empty when this is non-empty:
   * a photo with no caption is a real ask ("look at this"), not an empty send.
   */
  attachments?: ComposerAttachment[];
  threadId?: string;
  surface: SurfaceId;
  /**
   * The request's own id, one per ask: the web mints a UUID for every send.
   * The task-spawning capabilities dedupe on it, and the history tap stores it
   * as the composer_history row's id when it is a strict UUID (one id per
   * exchange, 2026-09-28), so the web's mirror row and the server row are one
   * row by id. Other callers name their own keys (the Line's `typed_…`, a
   * channel's `tg:…`), which are not UUIDs, so their rows keep a minted id.
   */
  idempotencyKey?: string;
  /**
   * The `idempotencyKey` of this person's earlier send that this one replaces:
   * the web cancels a send that has no answer yet when the next one goes, and
   * names it here. The server's steer table then answers the cancelled ask's
   * words and files together with these, in the order they were sent, when the
   * cancelled turn ended before it started work (a turn that did work already
   * answered for itself). Only ever looked up on the person's own slot, so it
   * can name nothing of anyone else's. Absent: nothing is replaced.
   */
  replaces?: string;
  /**
   * Client situation hints (untrusted; server-validated, only ever ADD words
   * to the turn's situation block, never gate anything):
   * - activeTaskId: the task open on the client surface (pre-existing).
   * - view: the in-app view id the user is looking at (web client stamps its
   *   route segment; server accepts only a closed allowlist).
   * - tz: the client's IANA timezone (Intl resolvedOptions().timeZone).
   * - front: the Mac app in front as the Dock or the notch saw it (Mac recipes R2): a HINT for
   *   which app's recipes Jev is offered. A pick for an app the words did not name runs only
   *   while that app is in front, and the runner checks it again.
   */
  context?: { activeTaskId?: string; view?: string; tz?: string; front?: { bundleId: string; title?: string } };
  /**
   * The composer pill's model pick — applied to any NEW task this turn spawns
   * (create_task / create_computer_task). Founder ruling 2026-07-16: an explicit
   * user pick beats the server's model-policy ladder. Optional: surfaces without
   * a picker (voice_card, moment_card, command_k) omit it and the policy decides.
   * Server-validated against the shared registry; an unknown/unavailable id
   * falls back to policy, never errors the turn.
   */
  taskModel?: string;
  /**
   * Where the requesting surface is EMBEDDED — 'native' when the webview runs
   * inside the desktop shell, 'web' in a plain browser. The surface id alone
   * cannot carry this ('composer' is the same input box in both hosts —
   * live-caught 2026-07-16: a browser ask typed into the DESKTOP composer
   * dispatched to the cloud, invisible pane). Placement folds host==='native'
   * into born-native so a desktop-composer task routes to the local runner
   * like a notch birth. Optional + fail-closed: absent (old clients, Slack,
   * mobile) means no machine claim — cloud placement as today.
   */
  host?: 'native' | 'web';
  /**
   * HOW the words arrived, independent of WHERE they were asked. The notch
   * takes both — ⌃-hold speaks, ⌃-tap opens the typed composer — and stays
   * surface 'voice_card' either way (it is one surface; ~20 native-behavior
   * predicates key off that id, and forking it would silently drop verbs,
   * gaze, capture, or local placement from typed asks).
   *
   * Only provenance changes: a spoken ask is transcribed and STT-normalized,
   * a typed ask is the user's own words and must pass verbatim. Optional and
   * back-compatible: absent means 'spoken', which is byte-identical to the
   * pre-composer wire (only 'voice_card' reads it at all).
   */
  inputMode?: 'typed' | 'spoken';
  /**
   * Agents first (train 1b): the HANDLE of the agent this turn is for (the
   * agent's `slug` on the person's own team). Absent = the lead, which is
   * every turn there was before this field existed. The server resolves it
   * (services/team/converse-agent.ts): an unknown, paused or retired handle
   * answers as the lead and the thread frame says so, it never errors.
   */
  agent?: string;
  /**
   * Server-only (spec 2026-09-22 §6): what the Line's stream reflex just did on the
   * person's Mac, so the mind continues rather than repeats it. Built by the Line;
   * the converse route's parser never copies a client-posted value.
   */
  lineActs?: { label: string; agoMs: number }[];
  /** Platform API v1: set only by the /v1 adapter; see ConversePlatformContext. */
  platform?: ConversePlatformContext;
}

export function isTerminalEvent(e: RouterEvent): boolean {
  return e.type === 'done' || e.type === 'error';
}

/**
 * The door a task can be born through (tasks.origin_channel). A strict subset
 * of SurfaceId: only the real messaging channels, never an in-app surface.
 */
export type OriginChannel = 'telegram' | 'whatsapp' | 'imessage' | 'slack' | 'email';

/**
 * Which channel a task is being BORN in, read off the composer request's
 * surface. Every in-app surface (composer, notch, command-K, mobile web, the
 * API) answers undefined — the honest "app-born" value the board renders as
 * no badge at all. Slack is a real messaging channel since @bash (spec
 * 2026-09-28 §17.1); its SurfaceId used to name an unused in-app surface.
 * Derived rather than hand-written at each spawn site so the task-creating
 * capabilities cannot drift from one another.
 */
export function originChannelFromSurface(surface: SurfaceId | undefined): OriginChannel | undefined {
  return surface === 'telegram' || surface === 'whatsapp' || surface === 'imessage' || surface === 'slack' || surface === 'email'
    ? surface
    : undefined;
}

/**
 * What each door is CALLED, for prose the user reads.
 *
 * A `Record<OriginChannel, …>` on purpose: it is exhaustive, so widening
 * `OriginChannel` without naming the new channel is a COMPILE ERROR rather than
 * a silent miss. That is the exact failure this replaced — the composer's
 * situation block hand-listed two channels as string comparisons, so an
 * iMessage turn fell through to "other" and lost its whole situation fact with
 * nothing to catch it.
 */
export const CHANNEL_DISPLAY_NAMES: Record<OriginChannel, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  imessage: 'iMessage',
  slack: 'Slack',
  email: 'Email',
};

/** The channels Bash OFFERS in prose (the reach question, the routine delivery menu). Slack is left out while @bash is dark (SLACK_CHANNEL_V1 + a fail-closed cohort): offering it to everyone would name a door most people cannot open. It joins when Slack graduates. The types (OriginChannel, CHANNEL_TYPES, deliverTo) include Slack: a linked Slack is a real delivery target. */
export const OFFERED_CHANNELS: readonly OriginChannel[] = ['telegram', 'whatsapp', 'imessage'];
